/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
// utils
import { format } from 'date-fns';
import { cn, getNextWorkingDay } from '@repo/zolintra-utils';
// components
import {
  Button,
  TextField,
  MessageBanner,
  DropdownList,
  InputsRadioGroup,
} from '@repo/zolintra-ui';
// validations
import { postRemotePostSchemaAny, postRemotePostSchemaOwn } from '../../../validations/postRemotePostSchema';
// hooks
import usePost from '../../../hooks/domain/usePost';
import { useDialog } from '../../../hooks/useDialog';
import useScrollTo from '../../../hooks/useScrollTo';
import useToast from '../../../hooks/useToast';
import useResources from '../../../hooks/domain/useResources';
import useSearchParams from '../../../hooks/useSearchParams';
import useFormUtils from '../../../hooks/useFormUtils';
import { useAppContext } from '../../../store/AppContext';
// permissions
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';
// services
import { postRemoteWorkingPost } from '../../../services/remoteWorkingPost/remoteWorkingPostService';
import { useResourcesListQuery, useResourcesStaffListQuery } from '../../../services/resource/resourceQueries';
// constants
import { REMOTE_WORKING_PLACES, VIEW_DOMAIN } from '../../../constants';

import styles from './Form.module.css';

const { HOME, OTHER } = REMOTE_WORKING_PLACES;

const nextWorkingDay = format(getNextWorkingDay(), 'yyyy-MM-dd');
const remoteWorkingFormDefaultValues = {
  resourceId: null,
  startDate: nextWorkingDay,
  endDate: nextWorkingDay,
  recurrence: null,
  place: HOME,
  address: '',
  comment: '',
  isExceptional: false,
};

function NewRemoteWorkingForm({ event, isAdminView }) {
  // Hooks
  const { t } = useTranslation();
  const [{ user }] = useAppContext();
  const {
    getSlotsIdFromDate,
    handleTwoYearsStraddleForOnePeriod,
    handleAPIErrorMessage,
    getTransgressedRulesCodesFromResponse,
  } = usePost();
  const { setDialog } = useDialog();
  const { scrollToFirstError } = useScrollTo();
  const toast = useToast();
  const { transformResourcesListToOptions, getStaffResourcesListWithUserInfo } = useResources();
  const { updateSearchParams } = useSearchParams();
  const { getFieldError } = useFormUtils();

  const hasCreateAnyAndResourcePermission = (
    hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
    && hasPermission(PERMISSIONS.CREATE_ANY_NEW_REMOTE_WORKING_POST, user.prm)
    && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
  );

  // Remote working form
  const {
    control,
    register,
    handleSubmit,
    formState: { errors: formErrors },
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      ...remoteWorkingFormDefaultValues,
      ...event,
      startDate: event?.startDate,
      endDate: event?.endDate,
    },
    resolver: joiResolver(
      hasPermission(PERMISSIONS.CREATE_ANY_NEW_REMOTE_WORKING_POST, user.prm)
        ? postRemotePostSchemaAny
        : postRemotePostSchemaOwn,
    ),
    mode: 'onBlur',
    shouldFocusError: false,
  });
  const placeValue = watch('place');
  const isExceptional = watch('isExceptional');
  const endDate = watch('endDate');

  const serializeFormValues = (values) => {
    const { startSlotId } = getSlotsIdFromDate(new Date(values.startDate));
    const { endSlotId } = getSlotsIdFromDate(new Date(values.endDate));

    return {
      resourceId: (
        hasPermission(PERMISSIONS.CREATE_ANY_NEW_REMOTE_WORKING_POST, user.prm)
          ? values.resourceId?.value
          : user.id
      ),
      includedStartSlotId: startSlotId,
      excludedEndSlotId: endSlotId + 1,
      recurrence: null,
      address: values.place === OTHER ? values.address : null,
      comment: values.comment,
      isExceptional: values.isExceptional,
    };
  };

  // API Calls
  const queryClient = useQueryClient();
  const resourcesListQuery = useResourcesListQuery({
    enabled: hasCreateAnyAndResourcePermission,
  });
  const resourcesStaffListQuery = useResourcesStaffListQuery({
    enabled: hasCreateAnyAndResourcePermission,
  });
  const resourcesListOptions = transformResourcesListToOptions(
    getStaffResourcesListWithUserInfo(
      resourcesStaffListQuery.data,
      resourcesListQuery.data,
    ),
  );

  const errorHasTransgressedRules = (error) => {
    // @ts-ignore - error is AxiosError, does have .response
    const transgressedRules = getTransgressedRulesCodesFromResponse(error.response);
    // enable exceptional request only if conditions permit
    const excludedCodesFromExceptional = [
      'single_post_per_resource_and_slot',
      'not_in_weekend_nor_public_holidays',
    ];

    if (
      transgressedRules.some((code) => excludedCodesFromExceptional.some(
        (excluded) => excluded === code,
      ))
    ) {
      return false;
    }

    return true;
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: (values) => postRemoteWorkingPost(
      {
        // @ts-ignore - can't use typing on mutationFn
        year: new Date(values.startDate).getFullYear().toString(),
      },
      serializeFormValues(values),
    ),
    onError: (e) => {
      if (errorHasTransgressedRules(e)) {
        setValue('isExceptional', true);
      }
    },
    throwOnError: (e) => {
      if (errorHasTransgressedRules(e)) {
        return false;
      }
      return true;
    },
    onSuccess: (_, values) => {
      queryClient.invalidateQueries({ queryKey: ['postsList'] });

      // @ts-ignore - can't use typing on onSuccess values
      const startDate = values?.startDate || null;

      // filter on start date
      if (startDate) {
        updateSearchParams({
          year: startDate.getFullYear(),
          month: startDate.getMonth(),
          view: VIEW_DOMAIN.REMOTE_WORKING,
        }, true);
      }

      setDialog({ type: 'HIDE_DIALOG' });

      toast.notify({
        type: 'success',
        message: t('remoteWorkingAndLeave.newRemoteWorkingForm.success'),
      });
    },
  });

  // Handlers
  const handleInputDateChange = (e, onChange) => {
    onChange(e);
    trigger(e.target.name);
  };

  const handleStartDateChange = ({ e, field }) => {
    handleInputDateChange(e, field.onChange);

    // automatically set values
    if (e.target.value) {
      const start = new Date(e.target.value).getTime();
      const end = endDate ? new Date(endDate).getTime() : null;

      // set end value based on start value
      // only if end value is empty or if the end date is before chosen start date
      if (!endDate || end < start) {
        setValue('endDate', e.target.value);
        trigger('endDate');
      }
    }
  };

  const handlePostSubmit = async (data) => {
    const postsValues = handleTwoYearsStraddleForOnePeriod(data);
    const promises = postsValues.map((values) => mutateAsync(values));
    await Promise.all(promises);
  };

  return (
    <div className={styles['form-container']}>
      <div className={styles['form-content']}>
        {hasPermission(PERMISSIONS.CREATE_ANY_NEW_REMOTE_WORKING_POST, user.prm) && isAdminView ? (
          <Controller
            name="resourceId"
            control={control}
            render={({ field }) => (
              <DropdownList
                {...field}
                label={t(
                  'remoteWorkingAndLeave.newRemoteWorkingForm.resource.label',
                )}
                placeholder={t(
                  'remoteWorkingAndLeave.newRemoteWorkingForm.resource.placeholder',
                )}
                options={resourcesListOptions}
                noOptionsMessage={t('common.noOptionsMessage')}
                className="margin-bottom-m"
                error={getFieldError({ errors: formErrors, fieldName: field.name })}
                isInsideDialog
                disabled={isExceptional}
              />
            )}
          />
        ) : null}

        <div className={cn([styles['two-columns-fields'], 'margin-bottom-m'])}>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <TextField
                type="date"
                label={t('remoteWorkingAndLeave.newRemoteWorkingForm.date.from')}
                error={getFieldError({ errors: formErrors, fieldName: field.name })}
                min={!hasPermission(PERMISSIONS.CREATE_POST_ANYTIME, user.prm) ? format(new Date(), 'yyyy-MM-dd') : ''}
                disabled={isExceptional}
                {...field}
                onChange={(e) => handleStartDateChange({ e, field })}
              />
            )}
          />
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <TextField
                type="date"
                label={t('remoteWorkingAndLeave.newRemoteWorkingForm.date.to')}
                error={getFieldError({ errors: formErrors, fieldName: field.name })}
                disabled={isExceptional}
                {...field}
                onChange={(e) => handleInputDateChange(e, field.onChange)}
              />
            )}
          />
        </div>

        <InputsRadioGroup
          {...register('place')}
          label={t('remoteWorkingAndLeave.newRemoteWorkingForm.place.label')}
          options={Object.values(REMOTE_WORKING_PLACES).map((value) => ({
            value,
            label: t(`remoteWorkingAndLeave.newRemoteWorkingForm.place.${value}`),
          }))}
          error={getFieldError({ errors: formErrors, fieldName: 'place' })}
          className="margin-bottom-m"
          disabled={isExceptional}
        />

        {placeValue === OTHER ? (
          <TextField
            label={t('remoteWorkingAndLeave.newRemoteWorkingForm.address.label')}
            placeholder={t(
              'remoteWorkingAndLeave.newRemoteWorkingForm.address.placeholder',
            )}
            className="margin-bottom-m"
            error={getFieldError({ errors: formErrors, fieldName: 'address' })}
            disabled={isExceptional}
            {...register('address')}
          />
        ) : null}

        {error ? (
          <MessageBanner color="error" icon="alarm" className="w-full">
            {handleAPIErrorMessage(error)}
          </MessageBanner>
        ) : null}

        {isExceptional ? (
          <>
            <TextField
              label={`${t(
                'remoteWorkingAndLeave.newRemoteWorkingForm.comment.label',
              )} (${t(
                'common.form.required',
              )})`}
              placeholder={t(
                'remoteWorkingAndLeave.newRemoteWorkingForm.comment.placeholder',
              )}
              className="margin-top-m"
              error={getFieldError({ errors: formErrors, fieldName: 'comment' })}
              {...register('comment')}
            />

            <MessageBanner color="primary" className="margin-top-m w-full">
              {t('remoteWorkingAndLeave.newRemoteWorkingForm.exceptionalInfos')}
            </MessageBanner>
          </>
        ) : null}
      </div>

      <div className={styles['submit-buttons']}>
        <Button variant="bordered" onClick={() => setDialog({ type: 'HIDE_DIALOG' })}>
          {t('common.cancel')}
        </Button>

        <Button
          onClick={handleSubmit(handlePostSubmit, scrollToFirstError)}
          isLoading={isPending}
        >
          {isExceptional
            ? t('remoteWorkingAndLeave.newRemoteWorkingForm.saveExceptional')
            : t('remoteWorkingAndLeave.newRemoteWorkingForm.save')}
        </Button>
      </div>
    </div>
  );
}

NewRemoteWorkingForm.propTypes = {
  event: PropTypes.shape({}),
  isAdminView: PropTypes.bool,
};

NewRemoteWorkingForm.defaultProps = {
  event: null,
  isAdminView: false,
};

export default NewRemoteWorkingForm;
